import { CSSProperties, JSX, useMemo, useState } from 'react'

import { groupBy, orderBy, partition, sumBy } from 'lodash'

import {
  CurrencyAmount,
  TokenBalance,
  TokenDisplay,
} from '~/components/component-library'
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '~/components/ui/hover-card'
import { SupportedChainId } from '~/constants/chains'
import { IAddress, OrgSmartAccount } from '~/types'
import { cn } from '~/utils/tailwind-utils'

type SegmentItem = {
  symbol: string
  address: IAddress
  chainId: SupportedChainId
  amount: bigint
  value?: number
  account?: IAddress
}

export type Segment = {
  name: string
  color: string
  value: number
  items: SegmentItem[]
}

type FullSegment = Segment & {
  percentage: number
}

// These 2 durations should be the same
const EMPHASIS_TRANSITION_DURATION = 'duration-300'
const SEGMENT_TOOLTIP_DELAY = 300

type SegmentedAssetsBarProps = {
  totalValue: number
  segments: Segment[]
  smartAccounts?: OrgSmartAccount[]
  showAllSegments?: boolean
  simplifiedTooltip?: boolean
}

export function SegmentedAssetsBar({
  totalValue,
  segments,
  smartAccounts,
  showAllSegments = false,
  simplifiedTooltip = false,
}: SegmentedAssetsBarProps) {
  const fullSegments = useMemo(() => {
    const segmentsWithPercentage: FullSegment[] = segments.map((segment) => ({
      ...segment,
      percentage: totalValue > 0 ? (segment.value / totalValue) * 100 : 0,
      items: orderBy(segment.items, ['value'], ['desc']),
    }))

    // When showAllSegments is true, return all segments without grouping into "Other"
    if (showAllSegments) {
      return segmentsWithPercentage
    }

    const [significantSegments, smallSegments] = partition(
      segmentsWithPercentage,
      (segment) => segment.percentage >= 1,
    )

    if (smallSegments.length > 0) {
      const otherSegment: FullSegment = {
        name: 'Other',
        color: '#888888',
        value: sumBy(smallSegments, 'value'),
        percentage: sumBy(smallSegments, 'percentage'),
        items: orderBy(
          smallSegments.flatMap((segment) => segment.items),
          ['value'],
          ['desc'],
        ),
      }

      return orderBy([...significantSegments, otherSegment], 'value', 'desc')
    }

    return orderBy(significantSegments, 'value', 'desc')
  }, [segments, totalValue, showAllSegments])

  // Tracks which segment is emphasized (others fade out)
  // hoveredSegment: immediate on mouse enter
  // tooltipSegment: maintains emphasis while tooltip is open
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null)
  const [tooltipSegment, setTooltipSegment] = useState<string | null>(null)
  const emphasizedSegment = hoveredSegment ?? tooltipSegment

  const isFaded = (segmentName: string): boolean =>
    !!emphasizedSegment && emphasizedSegment !== segmentName

  if (fullSegments.length === 0) {
    return null
  }

  return (
    <div className="w-full max-w-lg space-y-2">
      <div className="flex h-2.5 w-full flex-row space-x-px overflow-hidden rounded">
        {fullSegments.map((segment) => (
          <SegmentTooltip
            key={segment.name}
            segment={segment}
            smartAccounts={smartAccounts}
            simplifiedTooltip={simplifiedTooltip}
            style={{ width: `${segment.percentage}%` }}
            onOpenChange={(open) =>
              setTooltipSegment(open ? segment.name : null)
            }
          >
            <SegmentBlock
              segment={segment}
              isFaded={isFaded(segment.name)}
              onMouseEnter={() => setHoveredSegment(segment.name)}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          </SegmentTooltip>
        ))}
      </div>
      <div className="flex flex-row flex-wrap gap-x-3 gap-y-1">
        {fullSegments.map((segment) => (
          <div
            key={segment.name}
            onMouseEnter={() => setHoveredSegment(segment.name)}
            onMouseLeave={() => setHoveredSegment(null)}
          >
            <SegmentTooltip
              segment={segment}
              smartAccounts={smartAccounts}
              simplifiedTooltip={simplifiedTooltip}
              onOpenChange={(open) =>
                setTooltipSegment(open ? segment.name : null)
              }
            >
              <SegmentLegend
                segment={segment}
                isFaded={isFaded(segment.name)}
                isEmphasized={emphasizedSegment === segment.name}
              />
            </SegmentTooltip>
          </div>
        ))}
      </div>
    </div>
  )
}

type SegmentBlockProps = {
  segment: FullSegment
  isFaded: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
}

function SegmentBlock({
  segment,
  isFaded,
  onMouseEnter,
  onMouseLeave,
}: SegmentBlockProps) {
  return (
    <div
      className={cn(
        'h-full w-full overflow-hidden transition-opacity',
        EMPHASIS_TRANSITION_DURATION,
        isFaded && 'opacity-40',
      )}
      style={{ backgroundColor: segment.color }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  )
}

type SegmentLegendProps = {
  segment: FullSegment
  isFaded: boolean
  isEmphasized: boolean
}

function SegmentLegend({ segment, isFaded, isEmphasized }: SegmentLegendProps) {
  return (
    <div
      className={cn(
        'flex cursor-default flex-row items-center space-x-1 text-xs transition-opacity',
        EMPHASIS_TRANSITION_DURATION,
        isFaded && 'opacity-40',
      )}
    >
      <div
        className={cn(
          'h-3 w-3 rounded transition-transform',
          EMPHASIS_TRANSITION_DURATION,
          isEmphasized && 'scale-110',
        )}
        style={{ backgroundColor: segment.color }}
      />
      <span className="font-medium">{segment.name}</span>
    </div>
  )
}

type SegmentTooltipProps = {
  children: JSX.Element
  segment: FullSegment
  smartAccounts?: OrgSmartAccount[]
  simplifiedTooltip?: boolean
  style?: CSSProperties
  onOpenChange?: (open: boolean) => void
}

function SegmentTooltip({
  children,
  segment,
  smartAccounts,
  simplifiedTooltip,
  style = {},
  onOpenChange,
}: SegmentTooltipProps) {
  const content = simplifiedTooltip ? (
    <SimplifiedTooltipContent segment={segment} />
  ) : (
    <SegmentTooltipContent segment={segment} smartAccounts={smartAccounts} />
  )

  return (
    <HoverCard openDelay={SEGMENT_TOOLTIP_DELAY} onOpenChange={onOpenChange}>
      <HoverCardTrigger asChild>
        <div className="relative flex" style={style}>
          {children}
        </div>
      </HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side="bottom"
          sideOffset={8}
          collisionPadding={12}
          className="max-h-80 w-72 overflow-auto p-2.5 text-xs leading-relaxed"
        >
          {content}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  )
}

function SimplifiedTooltipContent({ segment }: { segment: FullSegment }) {
  const firstItem = segment.items[0]
  if (!firstItem) return null

  const totalAmount = segment.items.reduce((sum, item) => sum + item.amount, 0n)

  return (
    <TokenBalance
      address={firstItem.address}
      chainId={firstItem.chainId}
      tokenAmount={totalAmount}
      layout="standard"
      size="small"
      direction="neutral"
      showNetworkIcon
    />
  )
}

type SegmentTooltipContentProps = {
  segment: FullSegment
  smartAccounts?: OrgSmartAccount[]
}

function SegmentTooltipContent({
  segment,
  smartAccounts,
}: SegmentTooltipContentProps) {
  // Group by [symbol, chainId, address] to separate different tokens (e.g., earn positions from regular tokens)
  const groupedItems = groupBy(segment.items, (item) =>
    [item.symbol, item.chainId, item.address].join('_'),
  )
  // Sort by total value descending
  const groupedItemsEntries = orderBy(
    Object.entries(groupedItems),
    ([, items]) => sumBy(items, (item) => item.value ?? 0),
    'desc',
  )

  const hasAccountData = segment.items.some((item) => item.account)
  const showTotal = groupedItemsEntries.length > 1

  return (
    <div className="text-xs">
      <div className={cn('flex flex-col space-y-4', showTotal && 'pb-2')}>
        {groupedItemsEntries.map(([key, items]) => (
          <TokenChainSection
            key={key}
            items={items}
            smartAccounts={smartAccounts}
            hasAccountData={hasAccountData}
          />
        ))}
      </div>
      {showTotal && (
        <StickyTotalSection name={segment.name} value={segment.value} />
      )}
    </div>
  )
}

function StickyTotalSection({ name, value }: { name: string; value: number }) {
  return (
    <div className="sticky -bottom-2.5 z-10 -mx-2.5 -mb-2.5 flex flex-row items-center justify-between bg-surface-raised/90 px-2.5 pb-2.5 pt-2.5 text-sm font-medium backdrop-blur-sm">
      <span>Total {name}</span>
      <CurrencyAmount value={value} layout="inline" />
    </div>
  )
}

type TokenChainSectionProps = {
  items: SegmentItem[]
  smartAccounts?: OrgSmartAccount[]
  hasAccountData: boolean
}

function TokenChainSection({
  items,
  smartAccounts,
  hasAccountData,
}: TokenChainSectionProps) {
  const firstItem = items[0]
  if (!firstItem) return null

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0n)
  const totalValue = sumBy(items, (item) => item.value ?? 0)

  return (
    <div>
      <div className="flex w-full flex-row items-center justify-between">
        <TokenDisplay
          address={firstItem.address}
          chainId={firstItem.chainId}
          layout="two-line"
          size="small"
        />
        <TokenBalance
          address={firstItem.address}
          chainId={firstItem.chainId}
          tokenAmount={totalAmount}
          layout="standard"
          size="small"
          direction="neutral"
        />
      </div>
      {hasAccountData && (
        <AccountBreakdown
          items={items}
          smartAccounts={smartAccounts ?? []}
          totalValue={totalValue}
        />
      )}
    </div>
  )
}

type AccountBreakdownProps = {
  items: SegmentItem[]
  smartAccounts: OrgSmartAccount[]
  totalValue: number
}

function AccountBreakdown({
  items,
  smartAccounts,
  totalValue,
}: AccountBreakdownProps) {
  const groupedByAccount = groupBy(items, (item) => item.account!)

  return (
    <div className="mt-2 flex flex-col pl-6">
      {Object.entries(groupedByAccount).map(([account, accountItems]) => {
        const smartAccount = smartAccounts.find((sa) => sa.address === account)
        const accountValue = sumBy(accountItems, (item) => item.value ?? 0)
        const accountPercentage =
          totalValue === 0 ? 0 : ((accountValue / totalValue) * 100).toFixed(0)

        return (
          <div
            key={account}
            className="flex flex-row items-baseline gap-2 text-secondary"
          >
            <div className="flex flex-row items-center gap-1">
              <span>{smartAccount?.name ?? 'Account'}</span>
              <span className="text-secondary">{accountPercentage}%</span>
            </div>
            <div className="flex-grow border-b border-dashed border-default" />
            <div className="text-right font-medium">
              <CurrencyAmount value={accountValue} layout="inline" />
            </div>
          </div>
        )
      })}
    </div>
  )
}
